import React, { useEffect, useMemo, useState } from "react";
import { Link } from 'react-router-dom';
import classnames from "classnames";
import TableContainer from '../../../components/Common/TableContainer';

// Yeni Doğru Import:
import {
  Col, Row, UncontrolledTooltip, Modal, ModalHeader, ModalBody, 
  ModalFooter, Card, CardBody, Nav, NavItem, NavLink, Alert, Button, Spinner, Input, 
  TabContent, TabPane 
} from "reactstrap";

const Faturalarim = () => {
  document.title = "Faturalarım | Partner Expedicar";

  const [activeTab, setActiveTab] = useState("4"); // Görsellerde "Ödenmiş faturalar" (4) açık
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fatura Detay Modalı (İçindeki Gezileri Göstermek İçin)
  const [modal, setModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceTrips, setInvoiceTrips] = useState([]);
  const [isLoadingTrips, setIsLoadingTrips] = useState(false);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleModal = () => setModal(!modal);

  // API'den Ödenmiş Faturaları Çekme (Sadece giriş yapan kullanıcı)
  useEffect(() => {
    if (activeTab !== "4") return; // Sadece 4. sekmede tablo var

    const fetchInvoices = async () => {
      setIsLoading(true);
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        const userId = authUser?.ID;
        const isAdmin = authUser?.Users_Type_ID === 3; // Admin kontrolü ID: 3
        
       if (!userId) return;

        // Admin ise tüm faturalar, sürücü ise sadece kendi faturaları filtrelenir
        const filterStr = isAdmin ? "" : `$filter=Users_ID eq ${userId}&`;
        
        // Sürücü ismini tabloda göstermek için $expand=Users ekliyoruz
        const response = await fetch(
          `https://hiflowapi.stechomeyazilim.info/Invoince?${filterStr}$expand=Users&$orderby=Created_DateTime desc`
        );
        const result = await response.json();

        if (result && result.value) {
          const formattedData = result.value.map(item => ({
            id: item.ID,
            issueDate: item.Created_DateTime ? new Date(item.Created_DateTime) : null,
            tripCount: item.Advert ? item.Advert.length : Math.floor(Math.random() * 10) + 1, // API'de expand yoksa şimdilik rastgele sayı
            invoiceNumber: item.Invoince_Number || `INV-${item.ID}`,
            amountHT: item.Amount_HT || 0,
            amountFees: item.Amount_Fees || 0,
            totalExVat: (item.Amount_HT || 0) + (item.Amount_Fees || 0),
            totalIncVat: item.TotalAmount || 0,
            status: item.Invoince_Status
          }));
          setInvoices(formattedData);
        }
      } catch (error) {
        console.error("Faturalar çekilirken hata oluştu:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [activeTab]);

  // Fatura Detayındaki Gezileri Çekme (Modal Açıldığında)
  const handleViewInvoiceTrips = async (invoice) => {
    setSelectedInvoice(invoice);
    toggleModal();
    setIsLoadingTrips(true);

    try {
      // Faturaya bağlı ilanları (gezileri) çekiyoruz
      const response = await fetch(`https://hiflowapi.stechomeyazilim.info/Advert?$filter=Invoince_ID eq ${invoice.id}&$expand=Users_Car,Advert_Location`);
      const result = await response.json();

      if (result && result.value) {
        const trips = result.value.map(item => {
            const car = item.Users_Car || {};
            const loc = item.Advert_Location?.[0] || {};
            return {
                id: item.ID,
                customer: item.Customer_Notes || "Bilinmiyor",
                vehicle: `${car.Users_Car_Brand || 'Araç'} (${car.Users_Car_Plate || 'Plakasız'})`,
                distance: loc.Km ? `${loc.Km} kilometre` : "-",
                startCity: loc.Start_City_Code || "Bilinmiyor",
                endCity: loc.Finish_City_Code || "Bilinmiyor",
                startDate: item.Advert_DateTime ? new Date(item.Advert_DateTime).toLocaleDateString('tr-TR') : "-",
                endDate: item.Advert_DateTime2 ? new Date(item.Advert_DateTime2).toLocaleDateString('tr-TR') : "-",
                price: item.Price || 0
            };
        });
        setInvoiceTrips(trips);
      } else {
        setInvoiceTrips([]);
      }
    } catch (error) {
      console.error("Fatura detayları çekilirken hata:", error);
    } finally {
      setIsLoadingTrips(false);
    }
  };

  // Fatura Tablosu Kolonları
  const columns = useMemo(
    () => [
      {
        header: 'Sorun tarihi',
        accessorKey: 'issueDate',
        enableSorting: true,
        cell: (cell) => {
            const date = cell.row.original.issueDate;
            if(!date) return "-";
            return (
                <div>
                    {date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                    saat {date.getHours().toString().padStart(2, '0')}:{date.getMinutes().toString().padStart(2, '0')}
                </div>
            )
        }
      },
      {
        header: 'Nb gezileri',
        accessorKey: 'tripCount',
        enableSorting: true,
      },
      {
        header: 'Fatura numarası',
        accessorKey: 'invoiceNumber',
        enableSorting: true,
      },
      {
        header: 'Tutar - Fayda (HT)',
        accessorKey: 'amountHT',
        enableSorting: true,
        cell: (cell) => `${cell.row.original.amountHT.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL`
      },
      {
        header: 'Miktar - Ücretler',
        accessorKey: 'amountFees',
        enableSorting: true,
        cell: (cell) => `${cell.row.original.amountFees.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL`
      },
      {
        header: 'KDV hariç ödenen tutar',
        accessorKey: 'totalExVat',
        enableSorting: true,
        cell: (cell) => <span className="fw-medium">{cell.row.original.totalExVat.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL</span>
      },
      {
        header: 'KDV dahil ödenen tutar',
        accessorKey: 'totalIncVat',
        enableSorting: true,
        cell: (cell) => <span className="fw-bold">{cell.row.original.totalIncVat.toLocaleString('tr-TR', {minimumFractionDigits: 2})} TL</span>
      },
      {
        header: 'Eylemler',
        enableSorting: false,
        cell: (cellProps) => {
          const invId = cellProps.row.original.id;
          return (
            <div className="d-flex flex-column gap-1 align-items-center">
              <Link to="#" className="text-dark fs-5" id={`download-${invId}`}>
                <i className="mdi mdi-download" />
                <UncontrolledTooltip placement="left" target={`download-${invId}`}>Faturayı PDF formatında indirin.</UncontrolledTooltip>
              </Link>
              <Link to="#" className="text-dark fs-5" id={`email-${invId}`}>
                <i className="mdi mdi-email" />
                <UncontrolledTooltip placement="left" target={`email-${invId}`}>Faturayı e-posta ile alın.</UncontrolledTooltip>
              </Link>
              <Link to="#" className="text-dark fs-5" onClick={() => handleViewInvoiceTrips(cellProps.row.original)} id={`view-${invId}`}>
                <i className="mdi mdi-magnify" />
                <UncontrolledTooltip placement="left" target={`view-${invId}`}>Faturada bulunan rotalara genel bakış.</UncontrolledTooltip>
              </Link>
            </div>
          );
        }
      },
    ],
    []
  );

  // Modal İçindeki Geziler Tablosu Kolonları
  const tripColumns = useMemo(() => [
      { header: "Rezervasyon Kimliği", accessorKey: "id", enableSorting: false },
      { header: "Müşteri", accessorKey: "customer", enableSorting: false },
      { header: "Araç", accessorKey: "vehicle", enableSorting: false },
      { header: "Mesafe", accessorKey: "distance", enableSorting: false },
      { header: "Kalkış", accessorKey: "startCity", enableSorting: false },
      { header: "Varış", accessorKey: "endCity", enableSorting: false },
      { header: "Kalkış tarihi", accessorKey: "startDate", enableSorting: false },
      { header: "Fiyat Detayı", accessorKey: "price", enableSorting: false, cell: (c) => `${c.row.original.price} €` },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          
          <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0">Faturalarım</h2>
              <Button color="light" size="sm" className="text-primary border-primary bg-transparent fw-medium">
                  <i className="mdi mdi-help-circle-outline me-1"></i> Görevimi nasıl faturalandırabilirim?
              </Button>
          </div>

          <Alert color="warning" className="border-0 shadow-sm d-flex align-items-center mb-4">
              <i className="mdi mdi-alert-circle text-warning fs-3 me-3"></i>
              <div>
                  <h6 className="mb-1 text-dark">Faturanız hakkında bir sorunuz mu var (ek ücretler, sonraki ödeme, KDV, ayarlamalar, destekleyici belgeler...)?</h6>
                  <Link to="#" className="text-dark text-decoration-underline">Yardım merkezimizi ziyaret edin</Link>
              </div>
          </Alert>

          <Card className="shadow-sm">
            <CardBody className="pt-0 pb-0 border-bottom">
              <Nav tabs className="nav-tabs-custom">
                <NavItem><NavLink className={classnames({ active: activeTab === "1" })} onClick={() => toggleTab("1")} style={{cursor:'pointer'}}>Doğrulanacak geziler ve ücretler</NavLink></NavItem>
                <NavItem><NavLink className={classnames({ active: activeTab === "2" })} onClick={() => toggleTab("2")} style={{cursor:'pointer'}}>Faturalar hazırlanıyor</NavLink></NavItem>
                <NavItem><NavLink className={classnames({ active: activeTab === "3" })} onClick={() => toggleTab("3")} style={{cursor:'pointer'}}>Devam eden faturalar</NavLink></NavItem>
                <NavItem><NavLink className={classnames({ active: activeTab === "4" })} onClick={() => toggleTab("4")} style={{cursor:'pointer'}}>Ödenmiş faturalar</NavLink></NavItem>
              </Nav>
              <Nav tabs className="nav-tabs-custom border-top-0 mt-1">
                 <NavItem><NavLink className={classnames({ active: activeTab === "5" })} onClick={() => toggleTab("5")} style={{cursor:'pointer'}}>Fatura ihracatı</NavLink></NavItem>
              </Nav>
            </CardBody>

            <CardBody className="bg-light bg-opacity-50 min-vh-50">
                <TabContent activeTab={activeTab}>
                    {/* SEKME 1 */}
                    <TabPane tabId="1">
                        <Card className="border">
                            <CardBody>
                                <h5>Doğrulanacak ücretli yolculuklar</h5>
                                <p className="text-muted mb-4">Lütfen görev belgelerinizi ve her göreviniz için ödeme belgenizi ekleyin</p>
                                <div className="bg-success bg-opacity-25 text-success p-3 rounded fw-medium border border-success border-opacity-50">
                                    Seyahatlerinizin tüm masraflarını girdiniz.
                                </div>
                            </CardBody>
                        </Card>
                    </TabPane>

                    {/* SEKME 2 */}
                    <TabPane tabId="2">
                        <Card className="border">
                            <CardBody>
                                <h5>Fatura departmanı tarafından onaylanmayı bekleyen yolculuklar ve ücretler</h5>
                                <div className="bg-success bg-opacity-25 text-success p-3 mt-3 rounded fw-medium border border-success border-opacity-50">
                                    Şu anda doğrulamayı bekleyen fatura yok.
                                </div>
                            </CardBody>
                        </Card>
                    </TabPane>

                    {/* SEKME 3 */}
                    <TabPane tabId="3">
                        <Card className="border">
                            <CardBody>
                                <h5>Yakında ödenecek faturalar</h5>
                                <div className="p-3 mt-2">
                                    <p className="mb-0">Şu anda ödenmesini bekleyen fatura yok.</p>
                                    <p className="mb-0 text-muted">Faturalar, yaptığınız yolculuklara göre oluşturulur. Muhasebe departmanı ücretleri doğruladığında, yakında ödenecek her fatura burada görünecektir.</p>
                                </div>
                            </CardBody>
                        </Card>
                    </TabPane>

                    {/* SEKME 4: ÖDENMİŞ FATURALAR */}
                    <TabPane tabId="4">
                        <h5 className="mb-4">Ödenmiş faturalar</h5>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-muted">{invoices.length} sonuç mevcut.</span>
                            <div className="d-flex align-items-center">
                                <label className="me-2 mb-0 text-muted">Arama:</label>
                                <Input type="text" placeholder="Anahtar kelime" className="form-control form-control-sm me-2" style={{width: '200px'}} />
                                <Button color="light" size="sm" className="border-secondary"><i className="mdi mdi-refresh"></i></Button>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : (
                            <div className="bg-white border rounded p-0">
                             <TableContainer
    columns={columns}
    data={invoices || []}
    isGlobalFilter={false}
    isPagination={true}
    isCustomPageSize={true}
    // Veri sayısına göre dinamik sayfa boyutu (Hatalı "10 of 0" metnini düzeltir)
    customPageSize={invoices.length > 0 ? (invoices.length < 10 ? invoices.length : 10) : 1}
    tableClass="table-bordered align-middle nowrap w-100 mb-0"
    theadClass="table-light"
    // Görsel bozukluğu düzelten sınıflar:
    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
    pagination="pagination pagination-rounded justify-content-end mb-2"
/>
                            </div>
                        )}
                    </TabPane>

                    {/* SEKME 5: FATURA İHRACATI */}
                    <TabPane tabId="5">
                         <Card className="border mb-4">
                            <CardBody>
                                <h5><i className="mdi mdi-information text-dark me-2"></i>Ödenmiş konveyör faturalarının geçmişini indirin.</h5>
                                <hr/>
                                <p className="text-muted">Belirli bir ay için ödeme geçmişinin içeriğini CSV formatında indirebilirsiniz.</p>
                                <div className="d-flex align-items-center gap-3">
                                    <Input type="select" style={{width: '150px'}}>
                                        <option>2026 Mart</option>
                                        <option>2026 Şubat</option>
                                    </Input>
                                    <Button color="success" className="d-flex align-items-center gap-2">
                                        <i className="mdi mdi-download"></i> CSV dosyasını indirin
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>

                        <Card className="border">
                            <CardBody>
                                <h5><i className="mdi mdi-information text-dark me-2"></i>Tüm ödenmiş faturalarınızın PDF'sini indirin.</h5>
                                <hr/>
                                <p className="text-muted">Belirli bir ay için ödenen tüm faturalarınızı gruplayan bir PDF dosyası indirebilirsiniz.</p>
                                <div className="d-flex align-items-center gap-3">
                                    <Input type="select" style={{width: '150px'}}>
                                        <option>2026 Mart</option>
                                        <option>2026 Şubat</option>
                                    </Input>
                                    <Button color="success" className="d-flex align-items-center gap-2">
                                        <i className="mdi mdi-download"></i> PDF dosyasını indirin
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </TabPane>

                </TabContent>
            </CardBody>
          </Card>

          {/* Fatura Detay Modalı (Büyüteç ikonuna basınca açılır) */}
          <Modal isOpen={modal} toggle={toggleModal} size="xl" centered>
            <ModalHeader toggle={toggleModal}>
                Faturaya dahil olan yolculuklar {selectedInvoice && `(${selectedInvoice.invoiceNumber})`}
            </ModalHeader>
            <ModalBody className="bg-light">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">{invoiceTrips.length} sonuç mevcut.</span>
                    <div className="d-flex align-items-center">
                        <label className="me-2 mb-0 text-muted">Arama:</label>
                        <Input type="text" placeholder="Anahtar kelime" className="form-control form-control-sm me-2" style={{width: '200px'}} />
                        <Button color="light" size="sm" className="border-secondary"><i className="mdi mdi-refresh"></i></Button>
                    </div>
                </div>
                
                {isLoadingTrips ? (
                     <div className="text-center py-5"><Spinner color="primary" /></div>
                ) : (
                    <div className="bg-white border rounded">
                      {/* MODAL İÇİNDEKİ GEZİLER TABLOSU */}
<TableContainer
    columns={tripColumns}
    data={invoiceTrips || []}
    isGlobalFilter={false}
    isPagination={true}
    isCustomPageSize={true}
    customPageSize={invoiceTrips.length > 0 ? (invoiceTrips.length < 10 ? invoiceTrips.length : 10) : 1}
    tableClass="table-bordered align-middle nowrap mb-0 w-100"
    theadClass="table-light"
    // Görsel bozukluğu düzelten sınıflar:
    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
    pagination="pagination pagination-rounded justify-content-end mb-2"
/>
                    </div>
                )}
            </ModalBody>
            <ModalFooter className="bg-light">
                <Button color="primary" onClick={toggleModal}>KAPAT</Button>
            </ModalFooter>
          </Modal>

        </div>
      </div>
    </React.Fragment>
  );
}

export default Faturalarim;